import { useMutation } from "@tanstack/react-query";
import {
  getApiUrl,
  getQueryParamsString,
  getTransactionsExportPath,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { JWT_KEY } from "Utils/AuthUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import { format } from "date-fns";
import FileSaver from "file-saver";
import { toast } from "sonner";

type Params = {
  fileNamePrefix: string;
  userID?: number;
};

const fetchTransactionCsv = async (userID?: number) => {
  const queryParams = getQueryParamsString({
    targetUserId: userID,
    format: "Csv",
  });
  const route = `${getApiUrl()}${getTransactionsExportPath()}${queryParams}`;
  const response = await fetch(route, {
    method: "GET",
    mode: "cors",
    headers: { Authorization: getLocalStorageItem(JWT_KEY) ?? "" },
  });

  // Without this a 4xx/5xx would resolve and save the error body as a .csv.
  if (!response.ok) {
    throw new Error(
      `Transactions export failed with status ${response.status}`,
    );
  }

  return response;
};

export const useDownloadTransactionsExportMutation = () => {
  const { t } = useResource();

  return useMutation({
    mutationFn: async ({ userID, fileNamePrefix }: Params) => {
      const response = await fetchTransactionCsv(userID);
      const blob = await response.blob();
      const fileName = `${fileNamePrefix}_${format(new Date(), "yyyy-MM-dd")}.csv`;
      FileSaver.saveAs(blob, fileName);
    },
    onError: () => toast.error(t(Resources.Common.DownloadError)),
  });
};
